import { clsx } from 'clsx'
import { type FormEvent, useState } from 'react'

import { Button } from 'components/ui/Button'
import { TextField } from 'components/ui/TextField'
import { showToast } from 'components/ui/toaster'
import { useAuth } from 'contexts/AuthContext'
import { webLogger } from 'utils/logger'

type ScreenMode = 'signIn' | 'signUp'

type LocalhostEmailPasswordAuthProperties = {
  className?: string
}

export const LocalhostEmailPasswordAuth = ({ className = '' }: LocalhostEmailPasswordAuthProperties) => {
  /* --- Section 1: Hooks --- */
  const { loading, signInWithEmailAndPassword, signUpWithEmailAndPassword } = useAuth()

  /* --- Section 2: Local State --- */
  const [screen, setScreen] = useState<ScreenMode>('signIn')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  /* --- Section 3: Derived Values --- */
  const isBusy = loading || submitting
  const trimmedEmail = email.trim()

  /* --- Section 4: Event Handlers --- */
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!trimmedEmail || !password) {
      showToast('Enter email and password.', 'error')
      return
    }

    try {
      setSubmitting(true)
      await (screen === 'signIn'
        ? signInWithEmailAndPassword(trimmedEmail, password)
        : signUpWithEmailAndPassword(trimmedEmail, password))
    } catch (error) {
      webLogger.error(screen === 'signIn' ? 'Email sign-in error' : 'Email sign-up error', { error })
      showToast(
        screen === 'signIn'
          ? 'Sign in failed. Check your email and password.'
          : 'Could not create account. Try a different email.',
        'error'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleSwitchToSignUp = () => {
    setScreen('signUp')
  }

  const handleSwitchToSignIn = () => {
    setScreen('signIn')
  }

  /* --- Section 6: Render --- */
  return (
    <div className={clsx('auth-local-email', className)}>
      <form className="auth-local-email-form" onSubmit={handleSubmit}>
        <TextField
          autoComplete="email"
          disabled={isBusy}
          onChange={event => setEmail(event.target.value)}
          placeholder="Email"
          required
          type="email"
          value={email}
        />
        <TextField
          autoComplete={screen === 'signUp' ? 'new-password' : 'current-password'}
          disabled={isBusy}
          onChange={event => setPassword(event.target.value)}
          placeholder="Password"
          required
          type="password"
          value={password}
        />
        <Button disabled={isBusy} isLoading={isBusy} type="submit" variant="primary">
          {screen === 'signIn' ? 'Sign in' : 'Create account'}
        </Button>
      </form>
      {screen === 'signIn' ? (
        <Button
          className="auth-local-email-switch"
          disabled={isBusy}
          onClick={handleSwitchToSignUp}
          size="sm"
          type="button"
          variant="plain"
        >
          Create an Account
        </Button>
      ) : (
        <Button
          className="auth-local-email-switch"
          disabled={isBusy}
          onClick={handleSwitchToSignIn}
          size="sm"
          type="button"
          variant="plain"
        >
          Already have an account? Sign in
        </Button>
      )}
    </div>
  )
}
